// ===========================================
// SEAT CAROUSEL STORE
// ===========================================
// Minimal carousel navigation state for split hands.
// Extracted from gameTableRegistry.ts - only the nav slice the live Pixi path needs.
// ===========================================

import { create } from 'zustand';

// ===========================================
// TYPES
// ===========================================

/** Navigation state for a seat's split hands */
export interface SeatNavigationState {
  /** Currently viewed hand index (0-based) */
  viewedHandIndex: number;
  /** Direction of carousel slide animation */
  slideDirection: 'left' | 'right' | null;
  /** Total hands for this seat (used for navigation bounds) */
  totalHands: number;
}

interface SeatCarouselStore {
  // === STATE ===
  /** Navigation state per seat (for split hand carousel) */
  seatNavigation: Map<number, SeatNavigationState>;

  // === NAVIGATION METHODS ===
  /** Get navigation state for a seat */
  getSeatNavigation: (seatPosition: number) => SeatNavigationState;
  /** Set viewed hand index for a seat (with slide direction) */
  setSeatViewedHand: (seatPosition: number, handIndex: number, direction?: 'left' | 'right') => void;
  /** Set total hands for a seat (call when hands change) */
  setSeatTotalHands: (seatPosition: number, totalHands: number) => void;
  /** Navigate to next hand */
  navigateToNextHand: (seatPosition: number) => void;
  /** Navigate to previous hand */
  navigateToPreviousHand: (seatPosition: number) => void;
  /** Navigate directly to a specific hand */
  navigateToHand: (seatPosition: number, handIndex: number) => void;
  /** Reset navigation state for a seat (on new round) */
  resetSeatNavigation: (seatPosition: number) => void;
  /** Clear slide direction (after animation completes) */
  clearSlideDirection: (seatPosition: number) => void;
}

// ===========================================
// STORE
// ===========================================

export const useSeatCarouselStore = create<SeatCarouselStore>((set, get) => ({
  // === INITIAL STATE ===
  seatNavigation: new Map(),

  // === NAVIGATION METHODS ===

  getSeatNavigation: (seatPosition) => {
    const nav = get().seatNavigation.get(seatPosition);
    // Return existing or default state
    return nav || { viewedHandIndex: 0, slideDirection: null, totalHands: 1 };
  },

  setSeatViewedHand: (seatPosition, handIndex, direction) => {
    set((state) => {
      const newNav = new Map(state.seatNavigation);
      const current = newNav.get(seatPosition) || { viewedHandIndex: 0, slideDirection: null, totalHands: 1 };
      newNav.set(seatPosition, {
        ...current,
        viewedHandIndex: handIndex,
        slideDirection: direction || null,
      });
      console.log(`[SeatCarousel] Set seat ${seatPosition} viewedHand: ${handIndex}, direction: ${direction || 'none'}`);
      return { seatNavigation: newNav };
    });
  },

  setSeatTotalHands: (seatPosition, totalHands) => {
    set((state) => {
      const newNav = new Map(state.seatNavigation);
      const current = newNav.get(seatPosition) || { viewedHandIndex: 0, slideDirection: null, totalHands: 1 };

      // Clamp viewed index to valid range when total hands changes
      const clampedIndex = Math.min(current.viewedHandIndex, Math.max(0, totalHands - 1));

      newNav.set(seatPosition, {
        ...current,
        totalHands,
        viewedHandIndex: clampedIndex,
      });
      return { seatNavigation: newNav };
    });
  },

  navigateToNextHand: (seatPosition) => {
    const nav = get().getSeatNavigation(seatPosition);
    if (nav.totalHands <= 1) return;
    // Wrap around: last hand -> first hand
    const nextIndex = (nav.viewedHandIndex + 1) % nav.totalHands;
    get().setSeatViewedHand(seatPosition, nextIndex, 'left');
  },

  navigateToPreviousHand: (seatPosition) => {
    const nav = get().getSeatNavigation(seatPosition);
    if (nav.totalHands <= 1) return;
    // Wrap around: first hand -> last hand
    const prevIndex = (nav.viewedHandIndex - 1 + nav.totalHands) % nav.totalHands;
    get().setSeatViewedHand(seatPosition, prevIndex, 'right');
  },

  navigateToHand: (seatPosition, handIndex) => {
    const nav = get().getSeatNavigation(seatPosition);
    if (handIndex === nav.viewedHandIndex) return;

    // Clamp to valid range
    const clampedIndex = Math.max(0, Math.min(handIndex, nav.totalHands - 1));
    const direction = clampedIndex > nav.viewedHandIndex ? 'left' : 'right';
    get().setSeatViewedHand(seatPosition, clampedIndex, direction);
  },

  resetSeatNavigation: (seatPosition) => {
    set((state) => {
      const newNav = new Map(state.seatNavigation);
      newNav.set(seatPosition, { viewedHandIndex: 0, slideDirection: null, totalHands: 1 });
      console.log(`[SeatCarousel] Reset navigation for seat ${seatPosition}`);
      return { seatNavigation: newNav };
    });
  },

  clearSlideDirection: (seatPosition) => {
    set((state) => {
      const newNav = new Map(state.seatNavigation);
      const current = newNav.get(seatPosition);
      if (current) {
        newNav.set(seatPosition, { ...current, slideDirection: null });
      }
      return { seatNavigation: newNav };
    });
  },
}));

// ===========================================
// DIRECT ACCESS (for non-React code)
// ===========================================

/** Get carousel store state directly (for event handlers, animations) */
export function getSeatCarouselStore() {
  return useSeatCarouselStore.getState();
}

export default useSeatCarouselStore;
